import { MarkdownModule } from 'ngx-markdown';

import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouteReuseStrategy } from '@angular/router';

import { IonicModule, IonicRouteStrategy } from '@ionic/angular';

import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { HestiaNgrxLocalStorageSyncModule } from '@hestia/ngrx-local-storage-sync';
import { HestiaCommonModule } from '@hestia/ngx-common';
import { HestiaNgxFhirModule, fhirResourcesFeatureKey } from '@hestia/ngx-fhir';
import {
  NgxFhirQnrFormsModule,
  QnrSessionServerSaveStrategy,
  defaultTranslations,
  qnrFormSessionFeatureKey,
  qnrModuleSettingsFeatureKey,
} from '@hestia/ngx-fhir-qnr-forms';
import { HestiaNgxMitIdModule } from '@hestia/ngx-mitid';
import {
  AuthGuard,
  AuthNoneGuard,
  HestiaNgxUserModule,
  IsAuthenticatedRouteGuard,
  IsUnauthenticatedRouteGuard,
  userFeatureKey,
  HestiaInactivityInterceptor
} from '@hestia/ngx-user';

import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import {
  FinishCompletedRouteGuard,
  IntroCompletedRouteGuard,
  IntroNotCompletedRouteGuard,
} from './guards/intro-status.guard';
import { SdqCompletedRouteGuard, SdqNotCompletedRouteGuard } from './guards/sdq-status.guard';
import { initialFhirResources } from './models/fhir';
import { DawbaRoutes } from './route-constants';
import { DawbaAppService } from './services/app.service';
import { DawbaDevAdminService } from './services/dev-admin.service';
import { DawbaQnrInfoService } from './services/qnr-info.service';
import { reducers } from './store';
import { DawbaSpecificEffects } from './store/+dawba-specific/dawba-specific.effects';
import { dawbaSpecificFeatureKey } from './store/+dawba-specific/dawba-specific.reducer';
import { TranslocoRootModule } from './transloco-root.module';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';

const guards = [
  AuthGuard,
  AuthNoneGuard,
  IsUnauthenticatedRouteGuard,
  IsAuthenticatedRouteGuard,
  IntroCompletedRouteGuard,
  IntroNotCompletedRouteGuard,
  SdqCompletedRouteGuard,
  SdqNotCompletedRouteGuard,
  FinishCompletedRouteGuard,
];

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    IonicModule.forRoot(),
    HestiaCommonModule.forRoot(environment),
    HestiaNgxMitIdModule.forRoot({
      loginUrl: environment.mitIdLoginUrl,
      logoutUrl: environment.mitIdLogoutUrl,
      callbackRoute: environment.mitIdCallbackRoute,
    }),
    TranslocoRootModule,
    MarkdownModule.forRoot(),
    NgxFhirQnrFormsModule.forRoot({
      config: {
        enableWhenFunctions: {},
        enableDebugMode: false,
        rootPath: '/',
        globalMarkdown: true,
        qnrPageRoutePath: DawbaRoutes.QuestionnairePage,
        serverSaveStrategy: QnrSessionServerSaveStrategy.OnEveryPageSubmit,
        enableValueHistoryExt: true,
        enablePageNumber: false,
      },
      translations: defaultTranslations,
      userSettingsInitalState: {
        enableVirtualKeyboard: false,
      },
    }),
    StoreModule.forRoot(reducers, {
      initialState: {
        hydrated: false,
      },
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictStateSerializability: false,
        strictActionSerializability: false,
      },
    }),
    StoreDevtoolsModule.instrument({
      maxAge: 25,
      logOnly: environment.production,
    }),
    HestiaNgxUserModule.forRoot({
      syncHydratedKey: environment.syncHydratedKey,
      isAuthenticatedGuardFailureRoute: '/',
      isUnauthenticatedGuardFailureRoute: '/dashboard',
    }),
    HestiaNgxFhirModule.forRoot({
      initialResources: initialFhirResources,
    }),
    HestiaNgrxLocalStorageSyncModule.forRoot({
      storageKey: environment.syncStorageKey,
      syncProvider: 'local-forage',
      syncSettings: {
        keys: [
          userFeatureKey,
          qnrModuleSettingsFeatureKey,
          qnrFormSessionFeatureKey,
          fhirResourcesFeatureKey,
          dawbaSpecificFeatureKey,
        ],
        ignoreActions: [],
        hydratedStateKey: environment.syncHydratedKey, // Add this key to the state
      },
    }),
    EffectsModule.forRoot([DawbaSpecificEffects]),
  ],
  providers: [
    ...guards,
    DawbaAppService,
    DawbaDevAdminService,
    DawbaQnrInfoService,
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HestiaInactivityInterceptor,
      multi: true,
      deps: [HttpClient]
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
